import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, getFeatures, type User, type AuthConfig } from './api';

interface AuthState {
  user: User | null;
  loading: boolean;
  authConfig: AuthConfig | null;
  features: string[];
  hasFeature: (name: string) => boolean;
  requiresAuth: () => boolean;
  isInviteOnly: () => boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string, displayName: string, inviteToken?: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [features, setFeatures] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    try {
      const [u, config] = await Promise.allSettled([
        auth.me(),
        auth.getConfig(),
      ]);

      const resolvedUser = u.status === 'fulfilled' ? u.value : null;
      setUser(resolvedUser);
      setAuthConfig(
        config.status === 'fulfilled'
          ? config.value
          : { auth_enabled: true, invite_only: false, oauth_providers: [] },
      );

      // Load features when user is known
      if (resolvedUser) {
        const feats = await getFeatures().catch(() => []);
        setFeatures(feats);
      } else {
        setFeatures([]);
      }
    } catch {
      setUser(null);
      setAuthConfig({ auth_enabled: true, invite_only: false, oauth_providers: [] });
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const requiresAuth = useCallback(() => {
    // Default to true if config hasn't loaded yet
    return authConfig?.auth_enabled !== false;
  }, [authConfig]);

  const isInviteOnly = useCallback(() => {
    return authConfig?.invite_only === true;
  }, [authConfig]);

  const hasFeature = useCallback((name: string) => {
    // When auth is disabled, all features are available
    if (authConfig?.auth_enabled === false) return true;
    return features.includes(name);
  }, [features, authConfig]);

  const login = useCallback(async (email: string, password: string) => {
    const u = await auth.login(email, password);
    setUser(u);
    const feats = await getFeatures().catch(() => []);
    setFeatures(feats);
    return u;
  }, []);

  const register = useCallback(async (email: string, password: string, displayName: string, inviteToken?: string) => {
    const u = await auth.register(email, password, displayName, inviteToken);
    setUser(u);
    const feats = await getFeatures().catch(() => []);
    setFeatures(feats);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await auth.logout();
    setUser(null);
    setFeatures([]);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, authConfig, features, hasFeature, requiresAuth, isInviteOnly, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function isAdmin(user: User | null): boolean {
  return !!(user?.is_admin || user?.role === 'admin');
}
